import React, { useEffect, useState, useRef } from "react";
import { Table, Tag, Button, Popconfirm, message, Empty } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { fetchOffers, acceptOffer } from "../../utils/matchingApi";
import { ORDER_CREATED } from "../constants/status";

/**
 * Offers screen for a load (spec §5.3): carrier, truck, current location and
 * distance to pickup, offered rate, response time, Accept/Reject. Accepting
 * closes the loop: Offer → ACCEPTED, Trip created, load closed via the
 * Django API, the winning driver gets a push.
 */
const REFRESH_MS = 5000;

const OffersPanel = ({ orderId, orderStatus }) => {
  const [offers, setOffers] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [accepting, setAccepting] = useState(null);
  const timerRef = useRef(null);

  const isOpen = orderStatus === ORDER_CREATED;

  const load = async () => {
    if (!orderId || !isOpen) {
      return;
    }
    try {
      const data = await fetchOffers(orderId);
      setOffers(data?.offers ?? []);
      setHidden(false);
    } catch (err) {
      setHidden(true);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    const onRefresh = () => load();
    window.addEventListener("itruck:matching-refresh", onRefresh);
    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener("itruck:matching-refresh", onRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, orderStatus]);

  if (hidden || !isOpen) {
    return null;
  }

  const onAccept = async (offer) => {
    setAccepting(offer.id);
    try {
      await acceptOffer(orderId, offer.id);
      message.success("Перевозчик выбран. Груз закрыт, водителю отправлено уведомление.");
      window.dispatchEvent(new Event("itruck:matching-refresh"));
      load();
      // reload the page data so status/закрытие отобразилось
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      message.error(err?.message || "Не удалось принять предложение");
    } finally {
      setAccepting(null);
    }
  };

  const formatResponseTime = (seconds) => {
    if (seconds == null) {
      return "—";
    }
    if (seconds < 60) {
      return `${seconds} сек`;
    }
    if (seconds < 3600) {
      return `${Math.round(seconds / 60)} мин`;
    }
    return `${Math.round(seconds / 3600)} ч`;
  };

  const statusTag = (status) => {
    switch (status) {
      case "OPEN":
        return <Tag color="blue">Ждёт решения</Tag>;
      case "ACCEPTED":
        return <Tag color="green">Принято</Tag>;
      case "REJECTED":
        return <Tag color="red">Отклонено</Tag>;
      case "EXPIRED":
        return <Tag>Истекло</Tag>;
      case "WITHDRAWN":
        return <Tag>Отозвано</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const columns = [
    {
      title: "Перевозчик",
      key: "carrier",
      render: (_, o) => o.carrier?.name || `#${o.carrier_id}`,
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      width: 80,
      render: (score) =>
        score != null ? (
          <Tag color={score >= 85 ? "green" : score >= 70 ? "blue" : "orange"}>
            {score}
          </Tag>
        ) : (
          "—"
        ),
    },
    {
      title: "Где машина",
      key: "truck",
      render: (_, o) => (
        <span>
          {o.truck?.city || o.truck_city || "—"}
          {(o.distance_km != null || o.truck?.distance_km != null) && (
            <span className="matching-muted">
              {" "}
              · ~{Math.round(o.distance_km ?? o.truck?.distance_km)} км до загрузки
            </span>
          )}
        </span>
      ),
    },
    {
      title: "Предложение",
      key: "amount",
      render: (_, o) =>
        o.amount != null ? `${o.amount} ${o.currency || ""}` : "—",
    },
    {
      title: "Ответил за",
      dataIndex: "response_seconds",
      key: "response_time",
      render: formatResponseTime,
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      render: statusTag,
    },
    {
      title: "",
      key: "actions",
      width: 140,
      render: (_, o) =>
        o.status === "OPEN" ? (
          <Popconfirm
            title="Принять предложение?"
            description="Груз будет закрыт, другим перевозчикам придёт отказ."
            onConfirm={() => onAccept(o)}
            okText="Принять"
            cancelText="Отмена"
          >
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              loading={accepting === o.id}
            >
              Принять
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <div className="matching-panel">
      <div className="order-form__section__title">
        <span className="order-form__section__title__text">
          Предложения перевозчиков ({offers.length})
        </span>
      </div>
      {offers.length > 0 ? (
        <Table
          size="small"
          rowKey={(o) => o.id}
          columns={columns}
          dataSource={offers}
          pagination={false}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Пока нет предложений. Отклики водителей появятся здесь автоматически."
        />
      )}
    </div>
  );
};

export default OffersPanel;
